import React from 'react';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Analytics as AnalyticsIcon,
  PhotoLibrary as MediaIcon,
  Assessment as ReportsIcon,
  SmartToy as AIIcon,
  Create as CreateIcon,
  Article as ArticleIcon,
  Feed as BlogIcon,
  People as PeopleIcon,
  Mail as MailIcon,
  Forum as InboxIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

// Single source of truth for the app's navigation map — consumed by both
// the sidebar (grouped rendering) and the top-bar quick-jump search
// (flattened), so the two never drift out of sync.
export const getNavigation = ({ selectedBrand, user } = {}) => {
  const main = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Brands', icon: <BusinessIcon />, path: '/brands' },
  ];

  const bp = selectedBrand ? `/brands/${selectedBrand.brand_id}` : '';
  const brandGroups = selectedBrand ? [
    {
      title: 'Create',
      items: [
        { text: 'Composer', icon: <CreateIcon />, path: `${bp}/composer` },
        { text: 'Blog', icon: <BlogIcon />, path: `${bp}/blog` },
        { text: 'AI Assistant', icon: <AIIcon />, path: `${bp}/ai` },
        { text: 'Media Library', icon: <MediaIcon />, path: `${bp}/media` },
      ],
    },
    {
      title: 'Publish',
      items: [
        { text: 'Calendar', icon: <CalendarIcon />, path: `${bp}/calendar` },
        { text: 'Posts', icon: <ArticleIcon />, path: `${bp}/posts` },
        { text: 'Inbox', icon: <InboxIcon />, path: `${bp}/inbox` },
      ],
    },
    {
      title: 'Insights',
      items: [
        { text: 'Analytics', icon: <AnalyticsIcon />, path: `${bp}/analytics` },
        { text: 'Reports', icon: <ReportsIcon />, path: `${bp}/reports` },
      ],
    },
    {
      title: 'Brand',
      items: [
        { text: 'Brand Hub', icon: <StorefrontIcon />, path: bp },
        { text: 'Social Accounts', icon: <ShareIcon />, path: `${bp}/social-accounts` },
        { text: 'Integrations', icon: <LinkIcon />, path: `${bp}/integrations` },
      ],
    },
  ] : [];

  const admin = user?.role === 'admin' ? [
    { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin' },
    { text: 'Admin Users', icon: <PeopleIcon />, path: '/admin/users' },
    { text: 'Admin Brands', icon: <BusinessIcon />, path: '/admin/brands' },
  ] : [];

  const account = [
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Invitations', icon: <MailIcon />, path: '/invitations' },
  ];

  return { main, brandGroups, admin, account };
};

export const flattenNavigation = (nav) => [
  ...nav.main.map((i) => ({ ...i, group: 'Main' })),
  ...nav.brandGroups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.title }))),
  ...nav.admin.map((i) => ({ ...i, group: 'Administration' })),
  ...nav.account.map((i) => ({ ...i, group: 'Account' })),
];

// The single most specific nav item matching the current URL, so a parent
// route (e.g. Brand Hub at /brands/4) isn't active on its child pages.
export const findActivePath = (pathname, items) => {
  const matches = (path) =>
    path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
  return items.map((i) => i.path).filter(matches).sort((a, b) => b.length - a.length)[0];
};
